import React, { useState } from "react";
import { MOCK_YIELD_PRODUCTS } from "./mockData.js";

/* Treasury action modals — focused flows for each action.
   The Modal shell is reused across all action types; each action
   wires its own form. The Deploy modal renders the full 9-product
   registry with category filter + risk badges + below-min lockout.

   On confirm we call onSubmit(action, payload) so the parent owns
   the API call (treasury_*_execute, *_quote+execute, etc). The
   shells here keep the FE pixel-faithful while the back team
   ships §D endpoints. */

function Modal({ title, accent, onClose, children, footer }) {
  return (
    <div
      style={{ position:"fixed", inset:0, background:"rgba(8,11,16,0.72)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width:520, maxWidth:"92vw", maxHeight:"88vh", overflow:"auto",
          background:"var(--term-bg-pane)", border:`1px solid ${accent || "var(--term-line)"}`,
          boxShadow:`0 0 0 1px ${accent || "transparent"}22, 0 24px 60px rgba(0,0,0,0.6)`,
          display:"flex", flexDirection:"column",
        }}
      >
        <div style={{ padding:"12px 16px", borderBottom:"1px solid var(--term-line)", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ width:6, height:6, background: accent || "var(--term-ink-1)" }} />
            <span style={{ fontSize:11, letterSpacing:"0.16em", color:"var(--term-ink-1)", textTransform:"uppercase", fontWeight:600 }}>{title}</span>
          </div>
          <button onClick={onClose} style={{ background:"transparent", border:"1px solid var(--term-line)", color:"var(--term-ink-2)", padding:"3px 8px", fontSize:10, fontFamily:"inherit", cursor:"pointer" }}>ESC</button>
        </div>
        <div style={{ padding:16, flex:1 }}>{children}</div>
        {footer && <div style={{ padding:"12px 16px", borderTop:"1px solid var(--term-line)", display:"flex", justifyContent:"flex-end", gap:8, background:"var(--term-bg-pane-2)" }}>{footer}</div>}
      </div>
    </div>
  );
}

const inputStyle = {
  width:"100%", padding:"8px 10px",
  background:"var(--term-bg-base)", border:"1px solid var(--term-line)",
  color:"var(--term-ink-1)", fontFamily:"JetBrains Mono", fontSize:12, outline:"none",
};

const btn = (accent, primary) => ({
  padding:"7px 14px", fontSize:11, letterSpacing:"0.06em", fontFamily:"inherit",
  background: primary ? accent : "transparent",
  color: primary ? "#000" : "var(--term-ink-1)",
  border:`1px solid ${accent}`, cursor:"pointer", fontWeight:600,
});

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ fontSize:9, letterSpacing:"0.16em", color:"var(--term-ink-3)", textTransform:"uppercase", marginBottom:5, fontWeight:600 }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize:10, color:"var(--term-ink-3)", marginTop:4 }}>{hint}</div>}
    </div>
  );
}

function MoveModal({ onClose, onSubmit }) {
  const [from, setF] = useState("Acme Holdings B.V. · Operating");
  const [to, setT] = useState("Acme Capital LLC · Treasury");
  const [amt, setAmt] = useState("250000");
  const [ccy, setCcy] = useState("USD");
  const [rail, setRail] = useState("Internal");
  const fees = { Internal:0, FedWire:25, SEPA:0.5, SWIFT:35, USDC:1.5 };
  const ETA  = { Internal:"instant", FedWire:"same-day", SEPA:"1d", SWIFT:"1-3d", USDC:"~30s" };
  return (
    <Modal title="Move funds" accent="#4A8CFF" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#4A8CFF", true)} onClick={() => { onSubmit?.({ id:"move", from, to, amount:amt, currency:ccy, rail }); onClose(); }}>Initiate transfer</button>
      </>}>
      <Field label="From account">
        <select style={inputStyle} value={from} onChange={(e) => setF(e.target.value)}>
          <option>Acme Holdings B.V. · Operating</option>
          <option>Acme Capital LLC · Treasury</option>
          <option>Acme Asia Pte · Operating</option>
        </select>
      </Field>
      <Field label="To account">
        <select style={inputStyle} value={to} onChange={(e) => setT(e.target.value)}>
          <option>Acme Capital LLC · Treasury</option>
          <option>Acme Holdings B.V. · Operating</option>
          <option>External · Whitelisted</option>
        </select>
      </Field>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:8 }}>
        <Field label="Amount"><input style={inputStyle} value={amt} onChange={(e) => setAmt(e.target.value)} /></Field>
        <Field label="Currency">
          <select style={inputStyle} value={ccy} onChange={(e) => setCcy(e.target.value)}>
            <option>USD</option><option>EUR</option><option>GBP</option><option>USDC</option>
          </select>
        </Field>
      </div>
      <Field label="Rail">
        <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
          {["Internal","FedWire","SEPA","SWIFT","USDC"].map((r) => (
            <button key={r} onClick={() => setRail(r)} style={{ padding:"7px 12px", fontFamily:"inherit", fontSize:11, background: rail === r ? "#4A8CFF" : "var(--term-bg-base)", color: rail === r ? "#000" : "var(--term-ink-1)", border:"1px solid var(--term-line)", cursor:"pointer" }}>{r}</button>
          ))}
        </div>
      </Field>
      <div className="mono" style={{ border:"1px solid var(--term-line)", padding:12, fontSize:11, lineHeight:1.7 }}>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>ETA</span><span>{ETA[rail]}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Fee</span><span>${fees[rail]}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Approval</span><span>4-eyes · Treasurer + CFO</span></div>
      </div>
    </Modal>
  );
}

function SpotModal({ onClose, onSubmit }) {
  const [from, setF] = useState("USD");
  const [to, setT] = useState("EUR");
  const [amt, setAmt] = useState("1000000");
  const rates = { "USD-EUR":0.918, "USD-GBP":0.787, "USD-JPY":155.4, "USD-USDC":0.9998, "EUR-USD":1.089, "GBP-USD":1.270, "JPY-USD":0.00644, "USDC-USD":1.0002 };
  const rate = rates[`${from}-${to}`] || 1;
  const out = (parseFloat(amt) || 0) * rate;
  return (
    <Modal title="Spot convert" accent="#06B6D4" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#06B6D4", true)} onClick={() => { onSubmit?.({ id:"spot", from, to, amount:amt, rate }); onClose(); }}>Execute</button>
      </>}>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
        <Field label="From"><select style={inputStyle} value={from} onChange={(e) => setF(e.target.value)}><option>USD</option><option>EUR</option><option>GBP</option><option>USDC</option></select></Field>
        <Field label="To"><select style={inputStyle} value={to} onChange={(e) => setT(e.target.value)}><option>EUR</option><option>USD</option><option>GBP</option><option>JPY</option><option>USDC</option></select></Field>
      </div>
      <Field label="Amount" hint={`Mid rate ${rate} · spread 0.08% · fills in ~2s`}>
        <input style={inputStyle} value={amt} onChange={(e) => setAmt(e.target.value)} />
      </Field>
      <div className="mono" style={{ border:"1px solid var(--term-line)", padding:12, fontSize:11, lineHeight:1.7 }}>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>You send</span><span>{Number(amt || 0).toLocaleString()} {from}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>You receive</span><span style={{ color:"#06B6D4" }}>{out.toLocaleString(undefined, { maximumFractionDigits:2 })} {to}</span></div>
      </div>
    </Modal>
  );
}

function ProductCard({ p, selected, onPick }) {
  return (
    <button
      onClick={() => onPick(p.id)}
      style={{
        textAlign:"left", padding:"10px 12px", fontFamily:"inherit", fontSize:11,
        background: selected ? "#A78BFA1A" : "var(--term-bg-base)",
        color:"var(--term-ink-1)", border:`1px solid ${selected ? "#A78BFA" : "var(--term-line)"}`,
        cursor:"pointer", display:"grid", gridTemplateColumns:"1.4fr auto auto auto auto", gap:10, alignItems:"center",
      }}
    >
      <span style={{ display:"flex", flexDirection:"column", gap:2 }}>
        <span style={{ color:"var(--term-ink-1)", fontWeight:500 }}>{p.name}</span>
        <span style={{ color:"var(--term-ink-3)", fontSize:10 }}>{p.category} · {p.custody}</span>
      </span>
      <span className="mono" style={{ fontSize:10, color:"var(--term-ink-2)", padding:"2px 6px", border:"1px solid var(--term-line)", whiteSpace:"nowrap" }}>{p.yield_type}</span>
      <span className="mono" style={{ fontSize:10, color:p.risk_color_hint, padding:"2px 6px", border:`1px solid ${p.risk_color_hint}55`, whiteSpace:"nowrap" }}>{p.risk}</span>
      <span className="mono" style={{ fontSize:10, color:"var(--term-ink-2)", whiteSpace:"nowrap" }}>{p.lock_period_label}</span>
      <span className="mono" style={{ color:"#2CCB7E", fontSize:13, fontWeight:500, textAlign:"right", whiteSpace:"nowrap" }}>{p.target_apy.toFixed(2)}%</span>
    </button>
  );
}

function DeployModal({ onClose, onSubmit, products = MOCK_YIELD_PRODUCTS }) {
  const [stratId, setStratId] = useState(products[1]?.id);
  const [amt, setAmt] = useState("500000");
  const [filter, setFilter] = useState("All");
  const cats = ["All", ...Array.from(new Set(products.map((p) => p.category)))];
  const list = filter === "All" ? products : products.filter((p) => p.category === filter);
  const sel = products.find((s) => s.id === stratId) || products[0];
  const amount = parseFloat(amt) || 0;
  const min = parseFloat(sel?.minimum) || 0;
  const projected = amount * (sel?.target_apy || 0) / 100;
  const belowMin = amount > 0 && amount < min;
  return (
    <Modal title="Deploy to yield product" accent="#A78BFA" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#A78BFA", true)} disabled={belowMin || !sel} onClick={() => { onSubmit?.({ id:"deploy", product_id:sel.id, amount:amt, currency:sel.currency }); onClose(); }}>Deploy capital</button>
      </>}>
      <div style={{ display:"flex", gap:6, marginBottom:10, flexWrap:"wrap" }}>
        {cats.map((c) => (
          <button key={c} onClick={() => setFilter(c)} className="mono" style={{ padding:"4px 10px", fontSize:10, background: filter === c ? "var(--term-ink-1)" : "var(--term-bg-base)", color: filter === c ? "var(--term-bg-base)" : "var(--term-ink-2)", border:"1px solid var(--term-line)", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.04em" }}>{c}</button>
        ))}
      </div>
      <div className="mono" style={{ display:"grid", gridTemplateColumns:"1.4fr auto auto auto auto", gap:10, padding:"4px 12px", fontSize:9, color:"var(--term-ink-3)", textTransform:"uppercase", letterSpacing:"0.06em" }}>
        <span>Product</span><span>Type</span><span>Risk</span><span>Lock</span><span style={{ textAlign:"right" }}>Target APY</span>
      </div>
      <div style={{ display:"flex", flexDirection:"column", gap:4, maxHeight:280, overflow:"auto" }}>
        {list.map((p) => <ProductCard key={p.id} p={p} selected={stratId === p.id} onPick={setStratId} />)}
      </div>
      <Field label="Amount"><input style={inputStyle} value={amt} onChange={(e) => setAmt(e.target.value)} placeholder={sel ? `Min ${Number(sel.minimum).toLocaleString()} ${sel.currency}` : ""} /></Field>
      {sel && (
        <div className="mono" style={{ border:`1px solid ${belowMin ? "#FF6B6B55" : "var(--term-line)"}`, padding:12, fontSize:11, lineHeight:1.8, background: belowMin ? "#FF6B6B0E" : "transparent" }}>
          <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Selected</span><span>{sel.name}</span></div>
          <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Yield type</span><span>{sel.yield_type} · target {sel.target_apy.toFixed(2)}%</span></div>
          <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Risk / custody</span><span><span style={{ color:sel.risk_color_hint }}>{sel.risk}</span> · {sel.custody}</span></div>
          <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Lock / liquidity</span><span>{sel.lock_period_label} · {sel.liquidity}</span></div>
          <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Currency · minimum</span><span>{sel.currency} · {Number(sel.minimum).toLocaleString()}</span></div>
          <div style={{ display:"flex", justifyContent:"space-between", borderTop:"1px solid var(--term-line-dim)", paddingTop:6, marginTop:4 }}><span style={{ color:"var(--term-ink-3)" }}>Projected annual</span><span style={{ color:"#2CCB7E" }}>${projected.toLocaleString(undefined, { maximumFractionDigits:0 })}</span></div>
          {belowMin && <div style={{ color:"#FF6B6B", marginTop:6 }}>⚠ Below minimum · need {Number(sel.minimum).toLocaleString()} {sel.currency}</div>}
        </div>
      )}
    </Modal>
  );
}

function LendModal({ onClose, onSubmit }) {
  const [asset, setA] = useState("USDC");
  const [amt, setAmt]   = useState("500000");
  const [term, setTerm] = useState("30d");
  const APYS = { USDC:{ "7d":5.2, "30d":6.1, "90d":6.8 }, ETH:{ "7d":3.4, "30d":3.9, "90d":4.4 }, BTC:{ "7d":2.1, "30d":2.6, "90d":3.0 } };
  const apy = APYS[asset]?.[term] || 0;
  const days = term === "7d" ? 7 : term === "30d" ? 30 : 90;
  const earned = (parseFloat(amt) || 0) * apy / 100 * days / 365;
  return (
    <Modal title="Lend assets" accent="#FF5F6B" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#FF5F6B", true)} onClick={() => { onSubmit?.({ id:"lend", asset, amount:amt, term_days:days, apy_pct:apy }); onClose(); }}>Confirm lend</button>
      </>}>
      <Field label="Asset">
        <select style={inputStyle} value={asset} onChange={(e) => setA(e.target.value)}><option>USDC</option><option>ETH</option><option>BTC</option></select>
      </Field>
      <Field label="Amount"><input style={inputStyle} value={amt} onChange={(e) => setAmt(e.target.value)} /></Field>
      <Field label="Term">
        <div style={{ display:"flex", gap:6 }}>{["7d","30d","90d"].map((t) => (
          <button key={t} onClick={() => setTerm(t)} style={{ flex:1, padding:8, fontFamily:"inherit", fontSize:11, background: term === t ? "#FF5F6B" : "var(--term-bg-base)", color: term === t ? "#000" : "var(--term-ink-1)", border:"1px solid var(--term-line)", cursor:"pointer" }}>{t}</button>
        ))}</div>
      </Field>
      <div className="mono" style={{ border:"1px solid var(--term-line)", padding:12, fontSize:11, lineHeight:1.7 }}>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>APY</span><span style={{ color:"#2CCB7E" }}>{apy.toFixed(2)}%</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Projected earn</span><span>{earned.toFixed(2)} {asset}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Counterparty</span><span>Blox Lending Pool · A+</span></div>
      </div>
    </Modal>
  );
}

function RepayModal({ onClose, onSubmit }) {
  const LOANS = [
    { id:"L-2401", collateral:"5 BTC",  borrowed:"200,000 USDC", ltv_pct:62, apr_pct:6.4 },
    { id:"L-2398", collateral:"80 ETH", borrowed:"150,000 USDC", ltv_pct:55, apr_pct:5.8 },
  ];
  const [sel, setSel] = useState(LOANS[0].id);
  const [amt, setAmt] = useState("100000");
  return (
    <Modal title="Repay loan" accent="#FF5F6B" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#FF5F6B", true)} onClick={() => { onSubmit?.({ id:"repay", loan_id:sel, amount:amt }); onClose(); }}>Repay</button>
      </>}>
      <Field label="Outstanding loans">
        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>{LOANS.map((l) => (
          <button key={l.id} onClick={() => setSel(l.id)} className="mono" style={{ textAlign:"left", padding:"8px 10px", fontSize:11, background: sel === l.id ? "#FF5F6B22" : "var(--term-bg-base)", color:"var(--term-ink-1)", border:`1px solid ${sel === l.id ? "#FF5F6B" : "var(--term-line)"}`, cursor:"pointer", display:"grid", gridTemplateColumns:"auto 1fr 1fr auto", gap:8, fontFamily:"JetBrains Mono" }}>
            <span>{l.id}</span>
            <span style={{ color:"var(--term-ink-3)" }}>{l.collateral}</span>
            <span>{l.borrowed}</span>
            <span style={{ color:"#F5A524" }}>LTV {l.ltv_pct}%</span>
          </button>
        ))}</div>
      </Field>
      <Field label="Repay amount (USDC)"><input style={inputStyle} value={amt} onChange={(e) => setAmt(e.target.value)} /></Field>
    </Modal>
  );
}

function SweepModal({ onClose, onSubmit }) {
  const moves = [
    { entity:"Acme Holdings B.V. · Operating", target:"500000", current:"842300", destination_label:"→ Reserves", amount_moved:"342300" },
    { entity:"Acme Capital LLC · Treasury",    target:"200000", current:"412000", destination_label:"→ MMF",      amount_moved:"212000" },
    { entity:"Acme Asia Pte · Operating",      target:"100000", current: "88000", destination_label:"no action",  amount_moved: "0" },
  ];
  const total = moves.reduce((a, r) => a + (parseFloat(r.amount_moved) || 0), 0);
  const acting = moves.filter((r) => parseFloat(r.amount_moved) > 0).length;
  return (
    <Modal title="Sweep now" accent="#4A8CFF" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#4A8CFF", true)} onClick={() => { onSubmit?.({ id:"sweep", moves }); onClose(); }}>Execute sweep</button>
      </>}>
      <div style={{ fontSize:11, color:"var(--term-ink-2)", marginBottom:10, lineHeight:1.6 }}>
        Manual trigger of all active sweep rules. Below shows what will move (<b>${total.toLocaleString()}</b> across {acting} account{acting === 1 ? "" : "s"}).
      </div>
      <div style={{ border:"1px solid var(--term-line)" }}>
        {moves.map((r, i) => {
          const amt = parseFloat(r.amount_moved) || 0;
          return (
            <div key={i} className="mono" style={{ padding:"10px 12px", borderBottom: i < moves.length - 1 ? "1px solid var(--term-line-dim)" : "none", fontSize:11, display:"grid", gridTemplateColumns:"1.5fr 1fr 1fr", gap:8 }}>
              <span style={{ color:"var(--term-ink-1)" }}>{r.entity}</span>
              <span style={{ color:"var(--term-ink-3)" }}>${Number(r.current).toLocaleString()} → ${Number(r.target).toLocaleString()}</span>
              <span style={{ color: amt > 0 ? "#2CCB7E" : "var(--term-ink-3)", textAlign:"right" }}>
                {amt > 0 ? `+$${amt.toLocaleString()} ${r.destination_label}` : "—"}
              </span>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}

function ForwardModal({ onClose, onSubmit }) {
  const [ccy, setCcy] = useState("EUR");
  const [amt, setAmt] = useState("2000000");
  const [tenor, setTenor] = useState(30);
  // toy spread model: longer tenor => wider spread, larger amount => tighter spread
  const baseRate = ccy === "EUR" ? 1.0925 : ccy === "GBP" ? 1.270 : ccy === "JPY" ? 0.00644 : 1.0;
  const spreadBps = Math.min(35, 8 + tenor * 0.4 - Math.log10(Math.max(parseFloat(amt) || 1, 1)));
  const forwardRate = baseRate * (1 - spreadBps / 10000);
  const settleDate = new Date(Date.now() + tenor * 86400000).toISOString().slice(0, 10);
  return (
    <Modal title="Forward hedge" accent="#06B6D4" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#06B6D4", true)} onClick={() => { onSubmit?.({ id:"forward", currency:ccy, amount:amt, tenor_days:tenor, forward_rate:forwardRate.toFixed(5) }); onClose(); }}>Book forward</button>
      </>}>
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:8 }}>
        <Field label="Notional"><input style={inputStyle} value={amt} onChange={(e) => setAmt(e.target.value)} /></Field>
        <Field label="Currency">
          <select style={inputStyle} value={ccy} onChange={(e) => setCcy(e.target.value)}><option>EUR</option><option>GBP</option><option>JPY</option><option>SGD</option></select>
        </Field>
      </div>
      <Field label="Tenor">
        <div style={{ display:"flex", gap:6 }}>{[7, 30, 60, 90, 180].map((d) => (
          <button key={d} onClick={() => setTenor(d)} style={{ flex:1, padding:8, fontFamily:"inherit", fontSize:11, background: tenor === d ? "#06B6D4" : "var(--term-bg-base)", color: tenor === d ? "#000" : "var(--term-ink-1)", border:"1px solid var(--term-line)", cursor:"pointer" }}>{d}d</button>
        ))}</div>
      </Field>
      <div className="mono" style={{ border:"1px solid var(--term-line)", padding:12, fontSize:11, lineHeight:1.7 }}>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Forward rate</span><span style={{ color:"#06B6D4" }}>{forwardRate.toFixed(5)}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Spread</span><span>{spreadBps.toFixed(1)} bps</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Settlement</span><span>{settleDate}</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Approval</span><span>4-eyes · Treasurer</span></div>
      </div>
    </Modal>
  );
}

function BorrowModal({ onClose, onSubmit }) {
  const [collat, setC] = useState("BTC");
  const [collAmt, setCA] = useState("5");
  const [borrowAsset, setBA] = useState("USDC");
  const [borrowAmt, setBO] = useState("200000");
  const prices = { BTC:64200, ETH:3380, USDC:1 };
  const collValue = (parseFloat(collAmt) || 0) * prices[collat];
  const ltv = collValue ? ((parseFloat(borrowAmt) || 0) / collValue * 100) : 0;
  const liqLtv = 75; const apr = 6.4;
  const ltvColor = ltv > 65 ? "#FF5F6B" : ltv > 50 ? "#F5A524" : "#2CCB7E";
  return (
    <Modal title="Borrow against assets" accent="#FF5F6B" onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn("#FF5F6B", true)} onClick={() => { onSubmit?.({ id:"borrow", collateral_asset:collat, collateral_amount:collAmt, borrow_asset:borrowAsset, borrow_amount:borrowAmt, ltv_pct:ltv, apr_pct:apr }); onClose(); }}>Submit borrow</button>
      </>}>
      <Field label="Collateral asset">
        <select style={inputStyle} value={collat} onChange={(e) => setC(e.target.value)}><option>BTC</option><option>ETH</option></select>
      </Field>
      <Field label="Collateral amount" hint={`Mark price $${prices[collat].toLocaleString()} · value $${collValue.toLocaleString(undefined, { maximumFractionDigits:0 })}`}>
        <input style={inputStyle} value={collAmt} onChange={(e) => setCA(e.target.value)} />
      </Field>
      <Field label="Borrow asset">
        <select style={inputStyle} value={borrowAsset} onChange={(e) => setBA(e.target.value)}><option>USDC</option><option>USD</option><option>EUR</option></select>
      </Field>
      <Field label="Borrow amount"><input style={inputStyle} value={borrowAmt} onChange={(e) => setBO(e.target.value)} /></Field>
      <div className="mono" style={{ border:"1px solid var(--term-line)", padding:12, fontSize:11, lineHeight:1.7 }}>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>LTV</span><span style={{ color:ltvColor }}>{ltv.toFixed(1)}%</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>Liquidation LTV</span><span>{liqLtv}%</span></div>
        <div style={{ display:"flex", justifyContent:"space-between" }}><span style={{ color:"var(--term-ink-3)" }}>APR</span><span>{apr.toFixed(2)}%</span></div>
        <div style={{ marginTop:8, height:4, background:"var(--term-bg-base)", position:"relative" }}>
          <div style={{ position:"absolute", left:0, top:0, height:"100%", width:`${Math.min(ltv, 100)}%`, background:ltvColor }} />
          <div style={{ position:"absolute", left:`${liqLtv}%`, top:-2, bottom:-2, width:1, background:"#FF5F6B" }} />
        </div>
      </div>
    </Modal>
  );
}

function GenericModal({ id, label, accent, onClose, onSubmit }) {
  return (
    <Modal title={label} accent={accent} onClose={onClose}
      footer={<>
        <button style={btn("var(--term-line)")} onClick={onClose}>Cancel</button>
        <button style={btn(accent || "var(--term-ink-1)", true)} onClick={() => { onSubmit?.({ id }); onClose(); }}>Confirm</button>
      </>}>
      <div style={{ fontSize:11, color:"var(--term-ink-2)", lineHeight:1.7 }}>
        Action <b style={{ color:"var(--term-ink-1)" }}>{label}</b> ({id}) — this flow lights up when backend §D ships.
        It will include source/destination selectors, amount + currency, fee/rate preview, policy checks, approval routing, and confirmation.
      </div>
    </Modal>
  );
}

export default function TreasuryModal({ action, onClose, onSubmit, yieldProducts }) {
  if (!action) return null;
  if (action.id === "move")    return <MoveModal    onClose={onClose} onSubmit={onSubmit} />;
  if (action.id === "spot")    return <SpotModal    onClose={onClose} onSubmit={onSubmit} />;
  if (action.id === "deploy")  return <DeployModal  onClose={onClose} onSubmit={onSubmit} products={yieldProducts} />;
  if (action.id === "borrow")  return <BorrowModal  onClose={onClose} onSubmit={onSubmit} />;
  if (action.id === "lend")    return <LendModal    onClose={onClose} onSubmit={onSubmit} />;
  if (action.id === "repay")   return <RepayModal   onClose={onClose} onSubmit={onSubmit} />;
  if (action.id === "sweep")   return <SweepModal   onClose={onClose} onSubmit={onSubmit} />;
  if (action.id === "forward") return <ForwardModal onClose={onClose} onSubmit={onSubmit} />;
  return <GenericModal id={action.id} label={action.label} accent={action.accent} onClose={onClose} onSubmit={onSubmit} />;
}
